import { z } from 'zod'

/**
 * Tag domain logic. The slug is what makes a tag unique inside an org, so it
 * is derived here — once — and used identically by the API, the CSV importer
 * and the client-side duplicate warning.
 */

/**
 * "Small Group " and "small  group" collapse to `small-group`; accents are
 * preserved (a church tagging "Café Ministry" means it), punctuation is not.
 */
export function slugifyTag(name: string): string {
  return name
    .normalize('NFKC')
    .trim()
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
}

/** Accent names from `@church/ui-tokens`; clients map them to real colors. */
export const TAG_COLORS = [
  'slate',
  'brand',
  'green',
  'amber',
  'red',
  'purple',
  'teal',
  'pink',
] as const
export type TagColor = (typeof TAG_COLORS)[number]

const tagName = z
  .string()
  .trim()
  .min(1, 'Give the tag a name')
  .max(60)
  .refine((v) => slugifyTag(v) !== '', 'Use at least one letter or number')

export const createTagSchema = z.object({
  name: tagName,
  color: z.enum(TAG_COLORS).nullish(),
  description: z
    .string()
    .trim()
    .max(280)
    .nullish()
    .transform((v) => (v === '' || v === undefined ? null : v)),
})
export type CreateTagInput = z.infer<typeof createTagSchema>
export type CreateTagFormInput = z.input<typeof createTagSchema>

export const updateTagSchema = createTagSchema.partial()
export type UpdateTagInput = z.infer<typeof updateTagSchema>
export type UpdateTagFormInput = z.input<typeof updateTagSchema>

/** Bulk apply/remove. Capped because it runs inline, not as a job. */
export const bulkTagSchema = z.object({
  personIds: z.array(z.uuid()).min(1).max(500),
})
export type BulkTagInput = z.infer<typeof bulkTagSchema>
